import { GetRecoilValue } from "recoil";

import { SPECIFIC_SEARCH_PATTERN } from "../../constants";
import { IRideTableDto } from "../../dto";
import { getRidesIdsWithNearby, searchMatchers } from "../../helpers";
import {
  nearByFilterAtomFamily,
  nearbyFilteringRideIdAtom,
  nearbyOtherRidesIdsBatchAtom,
  starredTableRowsIdsAtomFamily,
} from "../atoms";

export const getNearbyFilteredRides = (
  get: GetRecoilValue,
  rides: IRideTableDto[],
): IRideTableDto[] => {
  const nearByFilterIsActive = get(nearByFilterAtomFamily("ride"));
  const filteringRideId = get(nearbyFilteringRideIdAtom);

  if (!nearByFilterIsActive && !filteringRideId) {
    return rides;
  }

  const nearbyOtherRidesIdsBatch = get(nearbyOtherRidesIdsBatchAtom);

  if (nearByFilterIsActive) {
    const ridesIdsWithNearby = getRidesIdsWithNearby(nearbyOtherRidesIdsBatch);
    const ridesIdsWithNearbySet = new Set(ridesIdsWithNearby);
    rides = rides.filter(x => ridesIdsWithNearbySet.has(x.id));
  }

  if (filteringRideId) {
    const otherNearbyRides =
      nearbyOtherRidesIdsBatch.data[filteringRideId] ?? [];
    const nearbyIds = [...otherNearbyRides, filteringRideId];
    const nearbyIdsSet = new Set(nearbyIds);
    rides = rides.filter(x => nearbyIdsSet.has(x.id));
  }

  return rides;
};

export const applySearchFilter = (
  rides: IRideTableDto[],
  searchValue: string,
  skipFilters: boolean,
): IRideTableDto[] => {
  if (
    !searchValue ||
    skipFilters ||
    searchValue.match(SPECIFIC_SEARCH_PATTERN)
  ) {
    return rides;
  }

  return rides.filter(
    row =>
      searchMatchers["metadata.passengersInfo"]({
        row,
        fieldName: "metadata.passengersInfo",
        searchValue,
      }) ||
      searchMatchers["defaultMatcher"]({
        row,
        fieldName: "id",
        searchValue,
      }),
  );
};

export const applyStarredLimit = ({
  get,
  rides,
  starredOnly,
  nonStarredOnly,
  skipFilters,
}: {
  get: GetRecoilValue;
  rides: IRideTableDto[];
  starredOnly: boolean;
  nonStarredOnly: boolean;
  skipFilters: boolean;
}): IRideTableDto[] => {
  // If no star-based mode is active, return as-is
  if (!starredOnly && !nonStarredOnly) {
    return rides;
  }

  const starredIds = get(starredTableRowsIdsAtomFamily("ride"));

  const starredIdsSet = new Set(starredIds);

  // First, exclude starred if non-starred-only mode is active
  const result = nonStarredOnly
    ? rides.filter(r => !starredIdsSet.has(r.id))
    : rides;

  // Then, if starred-only mode is active, include only starred
  if (starredOnly) {
    if (skipFilters) {
      return result.filter(r => starredIdsSet.has(r.id));
    }

    return starredIds.length
      ? result.filter(r => starredIdsSet.has(r.id))
      : result;
  }

  return result;
};
